import React, { useState, useEffect } from 'react';
import {
  ClipboardCheck,
  CheckCircle2,
  AlertTriangle,
  Fuel,
  Gauge,
  Truck,
  Send,
  History,
  Check,
  XCircle,
  Clock
} from 'lucide-react';
import { useAreaMotorista } from '../../contexts/AreaMotoristaContext';
import { ChecklistItem, VistoriaVeiculo } from '../../types/areaMotorista';
import { areaMotoristaService } from '../../services/areaMotoristaService';

const ITENS_PADRAO: Omit<ChecklistItem, 'status'>[] = [
  { id: '1', item: 'Pneus e Estepe (calibragem e desgaste)', categoria: 'seguranca' },
  { id: '2', item: 'Freios (resposta e nível de fluido)', categoria: 'mecanica' },
  { id: '3', item: 'Nível de Óleo do Motor', categoria: 'mecanica' },
  { id: '4', item: 'Água do Radiador / Arrefecimento', categoria: 'mecanica' },
  { id: '5', item: 'Faróis, Lanternas e Setas de Direção', categoria: 'seguranca' },
  { id: '6', item: 'Documentação do Veículo (CRLV em dia)', categoria: 'documentacao' },
  { id: '7', item: 'Equipamentos Obrigatórios (Triângulo, Macaco, Chave de Roda)', categoria: 'seguranca' },
  { id: '8', item: 'Limpeza e Higienização do Baú / Carroceria', categoria: 'limpeza' },
];

export const AreaMotoristaChecklist: React.FC = () => {
  const { currentMotorista, salvarVistoria } = useAreaMotorista();

  const [kmInicial, setKmInicial] = useState<number>(0);
  const [combustivel, setCombustivel] = useState<VistoriaVeiculo['nivel_combustivel']>('3/4');
  const [itensStatus, setItensStatus] = useState<Record<string, 'ok' | 'atencao' | 'critico'>>({});
  const [observacoes, setObservacoes] = useState('');
  const [historicoVistorias, setHistoricoVistorias] = useState<VistoriaVeiculo[]>([]);
  const [carregandoHistorico, setCarregandoHistorico] = useState(false);
  const [sucesso, setSucesso] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [abaAtiva, setAbaAtiva] = useState<'formulario' | 'historico'>('formulario');

  useEffect(() => {
    // Inicializa todos os itens como 'ok' por padrão
    const initialStatus: Record<string, 'ok' | 'atencao' | 'critico'> = {};
    ITENS_PADRAO.forEach(item => {
      initialStatus[item.id] = 'ok';
    });
    setItensStatus(initialStatus);

    if (currentMotorista) {
      setCarregandoHistorico(true);
      areaMotoristaService.getVistorias(currentMotorista.id)
        .then((vistorias) => {
          setHistoricoVistorias(vistorias);
          if (vistorias.length > 0 && vistorias[0].km_inicial) {
            setKmInicial(vistorias[0].km_inicial + 80); // Sugere o próximo KM
          }
        })
        .catch((err) => console.error('Erro ao carregar histórico de vistorias:', err))
        .finally(() => setCarregandoHistorico(false));
    }
  }, [currentMotorista]);

  const handleToggleStatus = (id: string, status: 'ok' | 'atencao' | 'critico') => {
    setItensStatus(prev => ({ ...prev, [id]: status }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentMotorista) return;

    const itensCompletos: ChecklistItem[] = ITENS_PADRAO.map(i => ({
      ...i,
      status: itensStatus[i.id] || 'ok'
    }));

    const temCritico = Object.values(itensStatus).includes('critico');

    const novaVistoria: VistoriaVeiculo = {
      id: `vistoria_${Date.now()}`,
      motorista_id: currentMotorista.id,
      veiculo_id: currentMotorista.veiculo_id || undefined,
      placa: currentMotorista.placa || undefined,
      data_vistoria: new Date().toISOString(),
      km_inicial: Number(kmInicial) || 0,
      nivel_combustivel: combustivel,
      itens: itensCompletos,
      aprovado: !temCritico,
      observacoes_gerais: observacoes.trim() || undefined,
      created_at: new Date().toISOString()
    };

    setErro(null);
    setEnviando(true);
    try {
      const ok = await salvarVistoria(novaVistoria);
      if (ok) {
        setSucesso(true);
        setHistoricoVistorias(prev => [novaVistoria, ...prev]);
        setTimeout(() => {
          setSucesso(false);
          setObservacoes('');
          setAbaAtiva('historico');
        }, 1500);
      } else {
        setErro('Não foi possível registrar a vistoria. Verifique sua conexão e tente novamente.');
      }
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto space-y-6 text-left">
      
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-black text-white flex items-center gap-2">
            <ClipboardCheck className="w-6 h-6 text-amber-400" />
            <span>Vistoria & Checklist Veicular</span>
          </h1>
          <p className="text-xs text-slate-400">
            Inspeção diária preventiva para garantir a segurança da operação.
          </p>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => setAbaAtiva('formulario')}
            className={`px-3.5 py-1.5 rounded-xl text-xs font-bold transition ${
              abaAtiva === 'formulario'
                ? 'bg-amber-600 text-white shadow-lg shadow-amber-600/30'
                : 'bg-slate-900 text-slate-400 hover:text-white border border-slate-800'
            }`}
          >
            Nova Vistoria
          </button>
          <button
            onClick={() => setAbaAtiva('historico')}
            className={`px-3.5 py-1.5 rounded-xl text-xs font-bold transition ${
              abaAtiva === 'historico'
                ? 'bg-amber-600 text-white shadow-lg shadow-amber-600/30'
                : 'bg-slate-900 text-slate-400 hover:text-white border border-slate-800'
            }`}
          >
            Histórico ({historicoVistorias.length})
          </button>
        </div>
      </div>

      {/* Main Container */}
      {abaAtiva === 'formulario' ? (
        <div className="rounded-3xl border border-slate-800 bg-slate-950/70 p-5 sm:p-6 shadow-2xl space-y-6">
          {sucesso ? (
            <div className="py-12 text-center space-y-3">
              <div className="w-16 h-16 bg-emerald-500/20 text-emerald-400 rounded-full flex items-center justify-center mx-auto animate-bounce">
                <Check className="w-8 h-8 stroke-[3]" />
              </div>
              <h2 className="text-lg font-black text-white">Vistoria Registrada com Sucesso!</h2>
              <p className="text-xs text-slate-400">Seu veículo está pronto para iniciar a operação com segurança.</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              
              {/* Vehicle & Initial KM */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-slate-400 uppercase tracking-wider flex items-center gap-1.5">
                    <Gauge className="w-4 h-4 text-amber-400" />
                    KM Atual do Painel <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    required
                    min={0}
                    value={kmInicial || ''}
                    onChange={(e) => setKmInicial(Number(e.target.value))}
                    placeholder="Ex: 145200"
                    className="w-full px-3 py-2 text-sm rounded-xl bg-slate-900 border border-slate-800 focus:border-amber-500 focus:ring-1 focus:ring-amber-500 text-white font-mono outline-none"
                  />
                </div>

                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-slate-400 uppercase tracking-wider flex items-center gap-1.5">
                    <Fuel className="w-4 h-4 text-amber-400" />
                    Nível do Combustível
                  </label>
                  <select
                    value={combustivel}
                    onChange={(e) => setCombustivel(e.target.value as any)}
                    className="w-full px-3 py-2 text-sm rounded-xl bg-slate-900 border border-slate-800 focus:border-amber-500 outline-none text-white font-medium"
                  >
                    <option value="reserva">⚠️ Na Reserva</option>
                    <option value="1/4">1/4 de Tanque</option>
                    <option value="1/2">1/2 (Meio Tanque)</option>
                    <option value="3/4">3/4 de Tanque</option>
                    <option value="cheio">⛽ Tanque Cheio</option>
                  </select>
                </div>
              </div>

              {/* Checklist Items */}
              <div className="space-y-3">
                <label className="text-xs font-bold text-slate-400 uppercase tracking-wider block">
                  Itens de Verificação Obrigatória
                </label>

                <div className="space-y-2">
                  {ITENS_PADRAO.map((item) => {
                    const status = itensStatus[item.id] || 'ok';
                    return (
                      <div
                        key={item.id}
                        className="p-3.5 rounded-2xl bg-slate-900/90 border border-slate-800 flex flex-col sm:flex-row sm:items-center justify-between gap-3 text-xs"
                      >
                        <span className="text-slate-200 font-semibold">{item.item}</span>

                        <div className="flex items-center gap-1.5 self-end sm:self-auto shrink-0">
                          <button
                            type="button"
                            onClick={() => handleToggleStatus(item.id, 'ok')}
                            className={`px-3 py-1 rounded-xl font-bold transition flex items-center gap-1 ${
                              status === 'ok'
                                ? 'bg-emerald-600 text-white shadow'
                                : 'bg-slate-800 text-slate-400 hover:text-white'
                            }`}
                          >
                            <Check className="w-3 h-3" />
                            <span>OK</span>
                          </button>

                          <button
                            type="button"
                            onClick={() => handleToggleStatus(item.id, 'atencao')}
                            className={`px-3 py-1 rounded-xl font-bold transition flex items-center gap-1 ${
                              status === 'atencao'
                                ? 'bg-amber-600 text-white shadow'
                                : 'bg-slate-800 text-slate-400 hover:text-white'
                            }`}
                          >
                            <AlertTriangle className="w-3 h-3" />
                            <span>Atenção</span>
                          </button>

                          <button
                            type="button"
                            onClick={() => handleToggleStatus(item.id, 'critico')}
                            className={`px-3 py-1 rounded-xl font-bold transition flex items-center gap-1 ${
                              status === 'critico'
                                ? 'bg-red-600 text-white shadow'
                                : 'bg-slate-800 text-slate-400 hover:text-white'
                            }`}
                          >
                            <XCircle className="w-3 h-3" />
                            <span>Crítico</span>
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* General Observations */}
              <div className="space-y-1.5">
                <label className="text-xs font-bold text-slate-400 uppercase tracking-wider block">
                  Observações / Barulhos / Detalhes (Opcional)
                </label>
                <textarea
                  rows={2}
                  value={observacoes}
                  onChange={(e) => setObservacoes(e.target.value)}
                  placeholder="Ex: Pneu dianteiro direito calibrado com 32 libras hoje pela manhã..."
                  className="w-full px-3 py-2 text-xs rounded-xl bg-slate-900 border border-slate-800 focus:border-amber-500 outline-none text-white resize-none"
                />
              </div>

              {/* Erro ao salvar */}
              {erro && (
                <div className="p-3 rounded-xl bg-red-950/60 border border-red-800 text-red-300 text-xs font-semibold flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 shrink-0" />
                  <span>{erro}</span>
                </div>
              )}

              {/* Submit CTA */}
              <button
                type="submit"
                disabled={enviando}
                className="w-full py-3.5 rounded-2xl bg-amber-600 hover:bg-amber-700 text-white font-black text-sm flex items-center justify-center gap-2 shadow-xl shadow-amber-600/30 transition active:scale-[0.99] disabled:opacity-60"
              >
                <Send className="w-4 h-4" />
                <span>{enviando ? 'Enviando...' : 'Registrar Vistoria Diária'}</span>
              </button>
            </form>
          )}
        </div>
      ) : (
        /* Histórico de Vistorias */
        <div className="rounded-3xl border border-slate-800 bg-slate-950/70 p-5 space-y-4 shadow-xl">
          <h2 className="text-sm font-bold text-white flex items-center gap-2">
            <History className="w-4 h-4 text-amber-400" />
            <span>Histórico de Vistorias Registradas</span>
          </h2>

          {carregandoHistorico ? (
            <div className="p-8 text-center text-xs text-slate-500">Carregando histórico...</div>
          ) : historicoVistorias.length === 0 ? (
            <div className="p-8 text-center text-xs text-slate-500 space-y-2">
              <ClipboardCheck className="w-8 h-8 mx-auto text-slate-600 stroke-[1.5]" />
              <p>Nenhuma vistoria registrada anteriormente.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {historicoVistorias.map((v) => (
                <div
                  key={v.id}
                  className="p-4 rounded-2xl bg-slate-900/80 border border-slate-800 space-y-2 text-xs"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="font-bold text-white">
                        {new Date(v.data_vistoria).toLocaleDateString('pt-BR')} • {new Date(v.data_vistoria).toLocaleTimeString('pt-BR').substring(0, 5)}
                      </span>
                      <span
                        className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                          v.aprovado
                            ? 'bg-emerald-950 text-emerald-400 border border-emerald-800'
                            : 'bg-red-950 text-red-400 border border-red-800'
                        }`}
                      >
                        {v.aprovado ? 'Aprovado' : 'Atenção / Crítico'}
                      </span>
                    </div>

                    <span className="font-mono text-slate-400">
                      KM: {v.km_inicial}
                    </span>
                  </div>

                  <div className="flex items-center gap-4 text-slate-400 text-[11px]">
                    <span>Combustível: <strong>{v.nivel_combustivel}</strong></span>
                    {v.placa && <span>Placa: <strong>{v.placa}</strong></span>}
                  </div>

                  {v.observacoes_gerais && (
                    <p className="text-slate-300 bg-slate-950 p-2 rounded-xl border border-slate-800/80 text-[11px]">
                      {v.observacoes_gerais}
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

    </div>
  );
};
